/**
 * The App Kit v1 contract between the host and an app's page. The state and
 * method shapes are what the page sees through `window.aibuild`, so within v1
 * they only ever grow: never remove or reinterpret a field (see v1/shipped-api.txt).
 */

export type KitRunStatus =
  | 'DRAFT'
  | 'RUNNING'
  | 'WAITING_ON_USER'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED';

export interface KitText {
  readonly id: string;
  readonly default: string;
}

export interface KitOptionChoice {
  readonly value: string;
  readonly label: KitText;
}

export interface KitOption {
  readonly id: string;
  readonly type: 'SELECT' | 'MULTISELECT' | 'TOGGLE' | 'NUMBER' | 'SLIDER' | 'TEXT';
  readonly label: KitText;
  readonly help?: KitText | null;
  readonly default: unknown;
  readonly choices: readonly KitOptionChoice[];
  readonly min?: number | null;
  readonly max?: number | null;
  readonly step?: number | null;
  readonly maxLength?: number | null;
  readonly unit?: string | null;
}

/** A file the page may read or download, by id; bytes come from `readFile`. */
export interface KitFile {
  readonly id: string;
  readonly name: string;
  readonly contentType: string;
  /** Null when not known, as for an example file. */
  readonly size: number | null;
  /** A renderable stand-in (a GLB for a STEP file), when one exists. */
  readonly previewId?: string | null;
  /** The stand-in's file name, whose extension picks the 3D loader. */
  readonly previewName?: string | null;
}

export interface KitApp {
  readonly key: string;
  readonly version: number;
  readonly name: KitText;
  readonly tagline: KitText;
  /** FILES when the files are the work, INSTRUCTION when the user's words are. */
  readonly start: 'FILES' | 'INSTRUCTION';
  /** False for a page that never starts a run, whose run methods the host refuses. */
  readonly usesAgent: boolean;
  readonly inputs: {
    readonly minFiles: number;
    readonly maxFiles: number;
    readonly acceptedExtensions: readonly string[];
  };
  readonly options: readonly KitOption[];
  readonly steps: readonly { readonly id: string; readonly label: KitText }[];
  readonly deliverable: {
    readonly extensions: readonly string[];
    readonly artifactTypes: readonly string[];
  };
  readonly example: {
    readonly inputs: readonly KitFile[];
    readonly result: KitFile | null;
    readonly options?: Readonly<Record<string, unknown>> | null;
    readonly prompt?: string | null;
  } | null;
}

export interface KitContext {
  /** The host's styled-components theme, so the kit and the page can match it. */
  readonly theme: Readonly<Record<string, unknown>>;
  readonly themeName: 'light' | 'dark';
  readonly locale: string;
  /** A colleague's run: viewable, not actionable. */
  readonly readOnly: boolean;
  /** A test run of the creator's draft. */
  readonly test: boolean;
  /** The builder's preview: nothing can be run. */
  readonly preview: boolean;
  /** Someone on a public link without an account: anything paid asks them to create one. */
  readonly visitor?: boolean;
}

export interface KitQuestion {
  readonly id: string;
  readonly question: string;
  readonly answers: readonly string[];
  readonly allowDecideForMe?: boolean;
  readonly allowFreeText?: boolean;
  /** A file id: the drawing the question points at. */
  readonly imageId?: string;
  readonly region?: readonly [number, number, number, number];
  readonly regionLabel?: string;
  /** The answer is a region the user draws on the image. */
  readonly expectsRegion?: boolean;
}

export interface KitResult {
  readonly version: number;
  readonly files: readonly KitFile[];
  /** A renderable stand-in for the version's model, when one exists. */
  readonly previewId?: string | null;
  readonly previewName?: string | null;
  readonly requestText?: string | null;
  readonly notesText?: string | null;
  readonly createdAt: string;
}

export interface KitTurn {
  readonly id: string;
  readonly requestText?: string | null;
  readonly replyText?: string | null;
  /** The version the turn produced; null for an answer in text alone. */
  readonly version: number | null;
  readonly createdAt: string;
}

export interface KitError {
  readonly code: string;
  readonly message: string;
}

export interface KitRun {
  readonly id: string;
  readonly status: KitRunStatus;
  /** The step the agent is in, live while a turn runs. */
  readonly stepId: string | null;
  readonly failedStepId: string | null;
  /** What the agent is doing right now, in words, while a turn runs. */
  readonly activity: string | null;
  /** A turn is in flight or on its way. */
  readonly busy: boolean;
  /** A stop was asked for and the run is winding down. */
  readonly stopping: boolean;
  readonly error: KitError | null;
  readonly options: Readonly<Record<string, unknown>>;
  readonly inputs: readonly KitFile[];
  readonly results: readonly KitResult[];
  readonly turns: readonly KitTurn[];
  /** The request of the turn in flight, which no turn records yet. */
  readonly pendingRequest: string | null;
  /** What the agent has stopped to ask; answering is the only reply it takes. */
  readonly questions: readonly KitQuestion[];
}

export interface KitState {
  readonly rev: number;
  readonly app: KitApp;
  readonly context: KitContext;
  /** Null before the first run of this app exists. */
  readonly run: KitRun | null;
}

export interface KitMark {
  readonly kind: 'box' | 'freehand';
  readonly points: readonly (readonly [number, number])[];
}

export interface KitAnswer {
  readonly question: string;
  readonly options: readonly string[];
  readonly answer: string;
  readonly answerType: string;
  readonly region?: readonly [number, number, number, number];
}

export interface KitStartParams {
  readonly prompt?: string;
  readonly options?: Readonly<Record<string, unknown>>;
  readonly files?: readonly File[];
  /** Which inputs to keep: on a draft, the ones it starts on; on a run that ran, the ones the new run takes. Omitted: as many as fit beside `files`. */
  readonly keepInputIds?: readonly string[];
  /** Run the app's example instead of the user's files. */
  readonly example?: boolean;
}

export interface KitRefineParams {
  readonly text?: string;
  /** Images that go with the turn, such as a render the user drew on. */
  readonly files?: readonly File[];
  readonly marks?: readonly KitMark[];
}

export interface KitAnswerParams {
  readonly answers: readonly KitAnswer[];
  /** Crops of the regions drawn as answers. */
  readonly files?: readonly File[];
}

export type KitErrorCode =
  | 'INVALID'
  | 'BUSY'
  | 'READ_ONLY'
  | 'NO_RUN'
  | 'NOT_FOUND'
  | 'TOO_LARGE'
  | 'NO_CREDITS'
  | 'RATE_LIMITED'
  | 'NEEDS_GESTURE'
  | 'UNSUPPORTED'
  | 'FAILED';

export const KIT_ERROR_CODES: readonly KitErrorCode[] = [
  'INVALID',
  'BUSY',
  'READ_ONLY',
  'NO_RUN',
  'NOT_FOUND',
  'TOO_LARGE',
  'NO_CREDITS',
  'RATE_LIMITED',
  'NEEDS_GESTURE',
  'UNSUPPORTED',
  'FAILED',
];

/** The view on screen, as a data URL, and how to render it. */
export interface KitViewRenderParams {
  readonly view: string;
  readonly width: number;
  readonly height: number;
  readonly model: string;
  readonly theme: string;
  readonly background: boolean;
}

export interface KitMethods {
  start: KitStartParams;
  refine: KitRefineParams;
  answer: KitAnswerParams;
  cancel: Record<string, never>;
  readFile: { readonly id: string };
  download: { readonly id: string };
  openTranscript: Record<string, never>;
  newRun: Record<string, never>;
  viewRenderOffer: Record<string, never>;
  renderView: KitViewRenderParams;
  requireAccount: Record<string, never>;
}

export type KitMethod = keyof KitMethods;

export const KIT_METHODS: readonly KitMethod[] = [
  'start',
  'refine',
  'answer',
  'cancel',
  'readFile',
  'download',
  'openTranscript',
  'newRun',
  'viewRenderOffer',
  'renderView',
  'requireAccount',
];

/** Methods that spend the viewer's credits, which the host lets through only on a user gesture. */
export const SPENDING_METHODS: readonly KitMethod[] = ['start', 'refine', 'answer'];

export const RUN_METHODS: readonly KitMethod[] = [...SPENDING_METHODS, 'newRun'];

/** Spends credits without a run, so it is gated like a run whether or not one exists. */
export const PAID_VIEW_METHODS: readonly KitMethod[] = ['renderView'];

export const MSG_REQUEST = 'aibuild:request';
export const MSG_REPLY = 'aibuild:reply';
export const MSG_STATE = 'aibuild:state';
export const MSG_ERROR = 'aibuild:error';
export const MSG_SNAPSHOT = 'aibuild:snapshot';
/** The longest a snapshot asked to `settle` waits for the page to stop changing before it is taken anyway. */
export const SNAPSHOT_SETTLE_MAX_MS = 8000;
export const MSG_VISIBILITY = 'aibuild:visibility';
/** Kit to host only, carrying the effective option values; not part of the page API. */
export const MSG_OPTIONS = 'aibuild:options';

export interface KitRequestMessage {
  readonly type: typeof MSG_REQUEST;
  readonly id: string;
  readonly method: KitMethod;
  readonly params: unknown;
}

export type KitReplyMessage =
  | { readonly type: typeof MSG_REPLY; readonly id: string; readonly ok: true; readonly value: unknown }
  | {
      readonly type: typeof MSG_REPLY;
      readonly id: string;
      readonly ok: false;
      readonly error: { readonly code: KitErrorCode; readonly message: string };
    };

/** Where an error came from: the kit's own code, or the app's page. */
export type KitErrorSource = 'kit' | 'page';

export const KIT_SOURCE_URL_PREFIX = 'aibuild-kit';
